/**
 * How much work the plan puts in each hour.
 *
 * Command and control's question before assigning anyone is not "what is in this request" — the
 * table answers that — it is "when does the load land". A request whose twenty-nine objects all
 * start within one hour needs a different number of engineers on shift than the same twenty-nine
 * spread across a day, and nothing on the screen said which.
 *
 * Deliberately an AGGREGATE: a count per hour, never a row per object. The table below is where
 * objects are enumerated.
 */

export interface PlannedRow {
    objectID?: string | null
    forecastStartTime?: string | Date | null
}

/** Whether an object could be handed to an engineer now. Injected so the strip uses the same
 *  rule as the queue rather than a second copy of it. */
export type CanAssign = (row: PlannedRow) => boolean

export interface PlannedHour {
    /** Start of the hour, local time. */
    hour: Date
    count: number
    /** Of that count, how many could be assigned now, and how many are waiting. */
    go: number
    wait: number
}

export interface PlannedTimeline {
    hours: PlannedHour[]
    /** The busiest hour's count — the bar scale, and the number worth reading. */
    peak: number
    total: number
    /** Objects with no forecast start. Counted, never silently dropped. */
    unscheduled: number
}

/** Hours beyond which the strip stops being readable; a longer plan is truncated and says so. */
export const MAX_HOURS = 72

function hourStart(d: Date): Date {
    const h = new Date(d)
    h.setMinutes(0, 0, 0)
    return h
}

export function plannedTimeline(rows: PlannedRow[], canAssign?: CanAssign): PlannedTimeline {
    const stamps: Array<{ d: Date; go: boolean }> = []
    let unscheduled = 0

    for (const r of rows || []) {
        if (!r?.forecastStartTime) { unscheduled += 1; continue }
        const d = new Date(r.forecastStartTime)
        if (Number.isNaN(d.getTime())) { unscheduled += 1; continue }
        stamps.push({ d, go: canAssign ? !!canAssign(r) : false })
    }

    if (stamps.length === 0) {
        return { hours: [], peak: 0, total: 0, unscheduled }
    }

    const counts = new Map<number, { n: number; go: number }>()
    for (const { d, go } of stamps) {
        const k = hourStart(d).getTime()
        const cur = counts.get(k) || { n: 0, go: 0 }
        cur.n += 1
        if (go) cur.go += 1
        counts.set(k, cur)
    }

    const first = Math.min(...counts.keys())
    const last = Math.max(...counts.keys())
    const span = Math.round((last - first) / 3_600_000) + 1

    // EVERY hour in the range, including the empty ones. A gap is information — it is when
    // nobody is needed — and collapsing it would make a quiet night look like a busy one.
    const hours: PlannedHour[] = []
    for (let i = 0; i < Math.min(span, MAX_HOURS); i++) {
        const t = first + i * 3_600_000
        const c = counts.get(t)
        const n = c?.n || 0
        const go = c?.go || 0
        hours.push({ hour: new Date(t), count: n, go, wait: n - go })
    }

    return {
        hours,
        peak: hours.reduce((m, h) => Math.max(m, h.count), 0),
        total: stamps.length,
        unscheduled,
    }
}

/** True when the plan runs past what the strip draws. */
export function isTruncated(rows: PlannedRow[]): boolean {
    const t = plannedTimeline(rows)
    if (t.hours.length === 0) return false
    return t.hours.length >= MAX_HOURS
}
